<template>
  <div>
    <p class="has-text-weight-bold is-size-4">{{ $t("mp:api_keys.title") }}</p>

    <div class="entity-details-card px-6 py-6 mb-5">
      <form @submit.prevent="saveWebhookUrl">
        <validation-observer ref="observer">
          <div class="is-flex">
            <BInputWithValidation
              style="width: 50%; margin-bottom: -2rem"
              :label="$t('mp:api_keys.webhook.label.webhook')"
              :rules="{
                required: true,
                regex:
                  /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/,
              }"
              label-position="on-border"
              v-model="webhookUrl"
            />

            <b-button type="is-blue" class="ml-auto" @click="saveWebhookUrl">
              {{ $t("mp:api_keys.webhook.button.save") }}
            </b-button>
          </div>
        </validation-observer>
      </form>
    </div>

    <template v-if="loaded">
      <FieldListing
        :data="apiKeys"
        :columns="tableColumns"
        :actions="actions"
        :searchable="false"
        :paginated="false"
        :backend-sorting="false"
      >
        <b-button
          v-if="canCreate"
          type="is-blue"
          @click="openCreateModal"
          icon-left="plus"
          class="is-pulled-right"
        >
          {{ $t("mp:api_keys.button.new") }}
        </b-button>

        <template slot="empty">{{ $t("mp:api_keys.table.empty") }}</template>
      </FieldListing>
    </template>

    <LoadingSkeleton v-else />
  </div>
</template>

<script>
import i18n from "../plugins/i18n";
import MerchantApiKeyCreateModal from "../components/modal/MerchantApiKeyCreateModal";
import LoadingSkeleton from "../components/LoadingSkeleton";
import FieldListing from "../components/FieldListing";
import { formatDateTime, resetValidator, localizedFormatter } from "../utils/util";
import { AUTHORITIES } from "../utils/authorities";
import BInputWithValidation from "../components/input/BInputWithValidation";
import { mapGetters } from "vuex/dist/vuex.common.js";

export default {
  name: "ApiKeys",
  title: "mp:page.api_keys.title",
  components: {
    FieldListing,
    LoadingSkeleton,
    BInputWithValidation,
  },
  async mounted() {
    await this.fetch();
    this.loaded = true;
    await resetValidator(this.$refs.observer);
  },
  data() {
    return {
      loaded: false,
      apiKeys: [],
      webhookUrl: null,
      tableColumns: [
        {
          field: "name",
          label: "mp:api_keys.list_header.name",
          format: (row) => {
            return row.name;
          },
        },
        {
          field: "created",
          label: "mp:api_keys.list_header.created",
          format: (row) => {
            if (row.created) {
              return localizedFormatter(this.country.languageIso).dateTime(row.created);
            }
            return "-";
          },
        },
        {
          field: "lastOperationId",
          label: "mp:api_keys.list_header.last_operation_id",
          format: (row) => {
            return this.$options.filters.empty(row.lastOperationId);
          },
        },
        {
          field: "lastAccess",
          label: "mp:api_keys.list_header.last_access",
          format: (row) => {
            if (row.lastAccess) {
              return localizedFormatter(this.country.languageIso).dateTime(row.lastAccess);
            }
            return "-";
          },
        },
      ],
      actions: [
        {
          icon: "trash-alt",
          iconPack: "fa",
          type: "is-danger",
          disabled: () => {
            return !this.canDelete;
          },
          function: async (row) => {
            this.deleteRow(row.id);
          },
        },
      ],
    };
  },
  computed: {
    ...mapGetters({
      country: "i18n/country",
    }),
    canCreate() {
      return this.$store.getters["session/hasAnyAuthority"]([
        AUTHORITIES.API_KEY.CREATE,
        AUTHORITIES.API_KEY.ADMIN,
      ]);
    },
    canDelete() {
      return this.$store.getters["session/hasAnyAuthority"]([
        AUTHORITIES.API_KEY.DELETE,
        AUTHORITIES.API_KEY.ADMIN,
      ]);
    },
  },
  methods: {
    async fetch() {
      await this.$store.dispatch("apiKeys/fetchList");
      this.apiKeys = this.$store.getters["apiKeys/list"];

      this.webhookUrl = await this.$store.dispatch("apiKeys/getWebhookUrl");
    },
    deleteRow(id) {
      this.$buefy.dialog.confirm({
        title: i18n.t("mp:api_keys.delete_confirm.title"),
        message: i18n.t("mp:api_keys.delete_confirm.message"),
        confirmText: i18n.t("mp:api_keys.delete_confirm.confirm"),
        cancelText: i18n.t("mp:api_keys.delete_confirm.cancel"),
        type: "is-danger",
        hasIcon: true,
        onConfirm: async () => {
          try {
            await this.$store.dispatch("apiKeys/delete", id);
            this.$buefy.toast.open({
              message: i18n.t("mp:api_keys.delete.success.message"),
              type: "is-success",
            });
            await this.fetch();
          } catch (ex) {
            console.error(ex);
            this.$buefy.toast.open({
              message: i18n.t("mp:api_keys.delete.failure.message"),
              type: "is-danger",
            });
          }
        },
      });
    },
    openCreateModal() {
      this.$buefy.modal.open({
        parent: this,
        component: MerchantApiKeyCreateModal,
        hasModalCard: true,
        trapFocus: true,
        canCancel: false,
        events: {
          save: async () => await this.fetch(),
        },
      });
    },
    async saveWebhookUrl() {
      const valid = await this.$refs.observer.validate();

      if (!valid) return;

      try {
        await this.$store.dispatch("apiKeys/updateWebhookUrl", {
          webhookUrl: this.webhookUrl,
        });

        this.$buefy.toast.open({
          message: i18n.t("mp:api_keys.webhook.message.save_successful"),
          type: "is-success",
        });

        await this.fetch();
      } catch (e) {
        this.$buefy.toast.open({
          message: i18n.t("mp:api_keys.webhook.message.save_failed"),
          type: "is-danger",
        });
      }
    },
  },
};
</script>
